import { useMemo } from 'react';
import { useSelector } from 'react-redux';

import { IsolatedKey } from 'Addons/fitmentSearch/isolatedKeys.ts';
import {
  createFitmentSearchRequestSelectionSelector,
  fitmentSearchBaseFieldsSelector,
} from 'Core/selectors/fitmentSearch/index.js';
import { createSelectRepeater, SelectContextProvider } from 'Components/smallComponents/select.tsx';
import { FacetValueBase } from 'Models/index.ts';
import fitmentSearchConfig from 'Models/uiConfig/fitmentSearchConfig.js';
import { equalBy } from 'Utils/array.js';
import { cloneSafe } from 'Utils/components.ts';

import type { FC } from 'react';
import type { InnerProps as Props } from './vehicleDropdowns.tsx';

const stub = () => {
  // stub
};

const StubDropdowns: FC<Props> = ({
  template,
  name,
  aboutToChange,
  appendedClasses,
  collapsing,
  fields,
  isolatedKey,
  isVehicleSelected,
  openDialog,
  rootRef,
  useNativeDropdown,
  useSearchableDropdown,
}) => {
  const fitmentBaseFields = useSelector(fitmentSearchBaseFieldsSelector);
  const selectFields = !fields || equalBy(fitmentBaseFields, fields, (v) => v) ? fitmentBaseFields : [];
  const vehicle = useVehicle(isolatedKey, selectFields);

  const selects = createSelectRepeater(
    selectFields.map((field: string, i: number) => ({
      entries: [{ value: vehicle[field] || '', term: '', selected: field in vehicle }],
      extraClassName: 'cm_vehicle-widget_select',
      disabled: i !== 0,
      loading: i === 0,
      hideNullOption: true,
      field,
      title: field,
      key: field,
      index: i,
      useNativeDropdown: !useSearchableDropdown && useNativeDropdown,
      isSearchable: useSearchableDropdown,
      onChange: () => {
        /* stub */
      },
    })),
  );

  const component = template.call({
    name,
    aboutToChange,
    selects,
    vehicle: {},
    vehicleString: '',
    vehicleBaseValues: [],
    vehicleExtraValues: [],
    allowToSetVehicle: false,
    allowToDiscardVehicle: false,
    changeVehicle: stub,
    selectVehicle: stub,
    discardVehicle: stub,
    facetToggle: () => null, // stub
    discardExtraFields: stub,
    discardFields: stub,
    fitmentSearchTitle: fitmentSearchConfig.fitmentSearchTitle,
    template: 'active',
    isVehicleSelected,
    selection: {},
    ...collapsing,
    isVehicleSelectionForced: false,
    openDialog,
  });

  return <SelectContextProvider>{cloneSafe(component, rootRef, { appendedClasses })}</SelectContextProvider>;
};

function useVehicle(isolatedKey: IsolatedKey, fields: string[]) {
  const selector = useMemo(() => createFitmentSearchRequestSelectionSelector(isolatedKey), [isolatedKey]);

  const selection = useSelector(selector) as FacetValueBase[];

  return useMemo(() => {
    return Object.fromEntries(
      fields
        .map((field) => [field, selection.find((v) => v.field === field)?.value])
        .filter(([, value]) => value) as [string, string][],
    );
  }, [selection, fields]);
}

export default StubDropdowns;
